import { useEffect, useMemo, useState } from 'react';
import { popiaApi, type DsrRequest, type DsrStatus } from '../../api';
import Select from '../../components/common/Select';
import { humanise } from '../../lib/labels';

// POPIA self-service - chambers' Information Officer (the owner by default)
// reviews and responds to data-subject requests submitted via the public
// /popia/submit endpoint. The data-pack helper assembles every personal-info
// holding for a single subject so the response can be vetted before sending.

const STATUS_OPTIONS: Array<{ value: DsrStatus | ''; label: string }> = [
  { value: '',             label: 'All statuses' },
  { value: 'received',     label: 'Received' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'in_progress',  label: 'In progress' },
  { value: 'resolved',     label: 'Resolved' },
  { value: 'refused',      label: 'Refused' },
];

export default function SettingsCompliance() {
  const [requests, setRequests] = useState<DsrRequest[]>([]);
  const [filter, setFilter] = useState<DsrStatus | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState<DsrRequest | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await popiaApi.list(filter || undefined);
      setRequests(r.requests);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const counts = useMemo(() => {
    const by: Record<string, number> = {};
    for (const r of requests) by[r.status] = (by[r.status] || 0) + 1;
    return by;
  }, [requests]);

  return (
    <div className="stack">
      <header>
        <h1>Compliance - POPIA</h1>
        <p className="muted">
          Data subject requests submitted via your chambers' public POPIA form. Each request must be acknowledged within
          a reasonable time and resolved within 30 days. Use the data pack to assemble what's held about a subject
          before responding.
        </p>
      </header>

      <div className="row" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
        {(['received', 'acknowledged', 'in_progress', 'resolved', 'refused'] as DsrStatus[]).map((s) => (
          <div key={s} className="card" style={{ cursor: 'pointer' }} onClick={() => setFilter(filter === s ? '' : s)}>
            <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{humanise(s)}</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: filter === s ? 'var(--color-accent)' : undefined }}>
              {counts[s] || 0}
            </div>
          </div>
        ))}
      </div>

      <div className="row">
        <div style={{ width: 200 }}>
          <Select value={filter} onChange={(v) => setFilter(v as DsrStatus | '')} options={STATUS_OPTIONS} />
        </div>
        <button onClick={load}>Refresh</button>
      </div>

      {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="muted" style={{ padding: 18 }}>Loading...</div>
        ) : requests.length === 0 ? (
          <div className="muted" style={{ padding: 18 }}>
            No requests {filter ? `with this status` : 'yet'}. Data subjects can submit at
            <code style={{ marginLeft: 6, fontSize: 12 }}>POST /server/api/popia/submit</code> with the chambers'
            tenant_id (a public form lives outside Litigate - typically embedded on the chambers' website).
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Kind</th>
                <th>Subject</th>
                <th>Submitted</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={String(r.ROWID)}>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{r.reference_code}</td>
                  <td><span className="pill">{humanise(r.kind)}</span></td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{r.subject_name}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{r.subject_email}</div>
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{r.submitted_at?.slice(0, 16).replace('T', ' ')}</td>
                  <td><span className={`pill ${statusStyle(r.status)}`}>{humanise(r.status)}</span></td>
                  <td style={{ textAlign: 'right' }}>
                    <button onClick={() => setOpened(r)}>Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {opened && (
        <RequestModal request={opened} onClose={() => { setOpened(null); load(); }} />
      )}
    </div>
  );
}

function statusStyle(s: DsrStatus): string {
  if (s === 'resolved')    return 'success';
  if (s === 'refused')     return 'danger';
  if (s === 'in_progress') return 'info';
  if (s === 'acknowledged')return 'accent';
  return '';
}

function RequestModal({ request, onClose }: { request: DsrRequest; onClose: () => void }) {
  const [status, setStatus] = useState<DsrStatus>(request.status);
  const [notes, setNotes] = useState(request.response_notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [packBusy, setPackBusy] = useState(false);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await popiaApi.update(request.ROWID, { status, response_notes: notes });
      onClose();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  const downloadPack = async () => {
    setPackBusy(true); setError(null);
    try {
      const data = await popiaApi.dataPack(request.ROWID);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `data-pack-${request.reference_code}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { setError((e as Error).message); }
    finally { setPackBusy(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card stack" style={{ width: 720, maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <header className="row">
          <div className="grow">
            <h3 style={{ margin: 0 }}>{request.reference_code}</h3>
            <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
              {humanise(request.kind)} - submitted {request.submitted_at?.slice(0, 10)}
            </div>
          </div>
          <button onClick={onClose}>Close</button>
        </header>

        <div className="card" style={{ background: 'var(--color-surface-2)' }}>
          <div className="row" style={{ alignItems: 'flex-start', gap: 24 }}>
            <div>
              <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subject</div>
              <div style={{ fontWeight: 600, marginTop: 2 }}>{request.subject_name}</div>
              <div className="muted" style={{ fontSize: 12 }}>{request.subject_email}</div>
              {request.subject_id_number && (
                <div className="muted" style={{ fontSize: 12 }}>ID: {request.subject_id_number}</div>
              )}
            </div>
            {request.related_party_id && (
              <div>
                <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Related party</div>
                <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, marginTop: 2 }}>#{request.related_party_id}</div>
              </div>
            )}
          </div>
          {request.details && (
            <div style={{ marginTop: 12 }}>
              <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Submitter's details</div>
              <div style={{ marginTop: 4, fontSize: 13, whiteSpace: 'pre-wrap' }}>{request.details}</div>
            </div>
          )}
        </div>

        <div className="row">
          <button onClick={downloadPack} disabled={packBusy}>
            {packBusy ? 'Building...' : 'Download data pack (JSON)'}
          </button>
          <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>
            Assembles every record this chambers holds about the subject. Review before sending.
          </span>
        </div>

        <div>
          <label>Status</label>
          <Select
            value={status}
            onChange={(v) => setStatus(v as DsrStatus)}
            options={STATUS_OPTIONS.filter((o) => o.value !== '') as { value: DsrStatus; label: string }[]}
          />
        </div>

        <div>
          <label>Response notes (internal)</label>
          <textarea
            rows={5}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What you found, what you did, what you sent the subject. Internal - only chambers members see this."
          />
        </div>

        {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={saving}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
